import { localApiClient } from "./localApiClient";

const norm = (value) => String(value || "").trim();
const lower = (value) => norm(value).toLowerCase();

const roleNames = (user = {}) =>
  (Array.isArray(user.userRoles) ? user.userRoles : [])
    .map((entry) => entry?.role?.name || entry?.name || entry?.roleName)
    .filter(Boolean);

const primaryRole = (user = {}) =>
  (Array.isArray(user.userRoles) ? user.userRoles : [])
    .map((entry) => entry?.role || entry)
    .find((role) => role?.id || role?.name) || null;

const primaryRoleName = (user = {}) => roleNames(user)[0] || user.roleName || "";

const permissionKeys = (user = {}) =>
  Array.from(
    new Set(
      (Array.isArray(user.userRoles) ? user.userRoles : [])
        .flatMap((entry) => entry?.role?.rolePermissions || entry?.rolePermissions || [])
        .map((entry) => entry?.permission?.key || entry?.key)
        .filter(Boolean)
    )
  );

const displayNameFromParts = (user = {}) =>
  user.displayName ||
  [user.lName, user.fName, user.oName]
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .join(" ") ||
  user.name ||
  user.email ||
  "";

export const fromLocalUser = (user = {}) => {
  const roleName = primaryRoleName(user);
  const role = primaryRole(user);
  const displayName = displayNameFromParts(user);
  const permissions = permissionKeys(user);
  return {
    ...user,
    id: user.id,
    hospitalId: user.hospitalId,
    email: user.email || "",
    displayName,
    name: displayName,
    fName: user.fName || "",
    lName: user.lName || "",
    oName: user.oName || "",
    gender: user.gender || "",
    phoneNumber: user.phoneNumber || "",
    passport: user.passport || null,
    employeeId: user.employeeId || "",
    departmentName: user.departmentName || "",
    clinicName: user.clinicName || "",
    completionDate: user.completionDate || "",
    employmentType: user.employmentType || "",
    medicalLicenseNumber: user.medicalLicenseNumber || "",
    yearsOfExperience: user.yearsOfExperience || "",
    professionalMemberships: user.professionalMemberships || "",
    emergencyContactPhone: user.emergencyContactPhone || "",
    emergencyContactEmail: user.emergencyContactEmail || "",
    roleId: role?.id || user.roleId || "",
    roleName,
    roleNames: roleNames(user),
    permissions,
    permissionKeys: permissions,
    specialty: user.specialty || "",
    status: user.status === "INACTIVE" || user.status === "DISABLED" ? "Disabled" : "Active",
    clinicId: user.clinicId || "",
    departmentId: user.departmentId || "",
  };
};

const isDoctor = (user = {}) => {
  const haystack = [user.roleName, ...(user.roleNames || []), user.specialty, user.displayName, user.name].map(lower).join(" ");
  return /doctor|consultant|physician|clinician|medical officer|mo\b/.test(haystack);
};

const wrapLocalError = (action, error) => {
  const message = error?.message || String(error);
  if (/bearer|jwt|unauthor/i.test(message)) {
    throw new Error(`${action} failed: Local API token is missing or expired.`);
  }
  if (/forbidden|permission/i.test(message)) {
    throw new Error(`${action} failed: Your local account does not have permission for this action.`);
  }
  if (/email already exists|phone number already exists|unique/i.test(message)) {
    throw new Error(`${action} failed: A local user with this email or phone already exists.`);
  }
  if (/inactive/i.test(message)) {
    throw new Error(`${action} failed: This local user account is inactive.`);
  }
  if (/failed to fetch|network/i.test(message)) {
    throw new Error(`${action} failed: Local API is unavailable.`);
  }
  throw new Error(`${action} failed: ${message}`);
};

const filterByClinicIfAvailable = (users = [], clinicId = "") => {
  if (!clinicId) return users;
  const clinicScoped = users.filter((user) => user.clinicId === clinicId);
  return clinicScoped.length ? clinicScoped : users;
};

const clean = (value) => {
  const text = String(value || "").trim();
  return text || undefined;
};

const toLocalUserPayload = (hospitalId, userData = {}) => {
  const displayName = displayNameFromParts(userData);
  const phonePassword = String(userData.phoneNumber || "").replace(/\D/g, "");
  const payload = {
    hospitalId,
    email: clean(userData.email)?.toLowerCase(),
    displayName,
    fName: clean(userData.fName),
    lName: clean(userData.lName),
    oName: clean(userData.oName),
    gender: clean(userData.gender),
    phoneNumber: clean(userData.phoneNumber),
    passport: typeof userData.passport === "string" ? userData.passport : undefined,
    employeeId: clean(userData.employeeId),
    clinicId: clean(userData.clinicId),
    clinicName: clean(userData.clinicName),
    departmentId: clean(userData.departmentId),
    departmentName: clean(userData.departmentName),
    completionDate: clean(userData.completionDate),
    employmentType: clean(userData.employmentType),
    specialty: clean(userData.specialty),
    medicalLicenseNumber: clean(userData.medicalLicenseNumber),
    yearsOfExperience: clean(userData.yearsOfExperience),
    professionalMemberships: clean(userData.professionalMemberships),
    emergencyContactPhone: clean(userData.emergencyContactPhone),
    emergencyContactEmail: clean(userData.emergencyContactEmail)?.toLowerCase(),
    roleId: clean(userData.roleId),
    roleName: clean(userData.roleName),
  };

  if (!userData.id) {
    payload.password = userData.password || (phonePassword.length >= 6 ? phonePassword : undefined);
  }

  Object.keys(payload).forEach((key) => payload[key] === undefined && delete payload[key]);
  return payload;
};

export const localUserService = {
  async createUser(userData = {}) {
    try {
      const saved = await localApiClient.post("/users", userData);
      return fromLocalUser(saved);
    } catch (error) {
      wrapLocalError("Create local user", error);
    }
  },

  async saveUser(hospitalId, userData = {}) {
    try {
      if (!hospitalId) throw new Error("Hospital ID is required.");
      if (!userData.email) throw new Error("Email address is required.");
      if (!userData.id && !userData.password && String(userData.phoneNumber || "").replace(/\D/g, "").length < 6) {
        throw new Error("Enter a phone number or password to create the local user.");
      }
      const payload = toLocalUserPayload(hospitalId, userData);
      const saved = userData.id
        ? await localApiClient.patch(`/users/${encodeURIComponent(hospitalId)}/${encodeURIComponent(userData.id)}`, payload)
        : await localApiClient.post("/users", payload);
      return fromLocalUser(saved);
    } catch (error) {
      wrapLocalError(userData.id ? "Update local user" : "Create local user", error);
    }
  },

  async updateUser(hospitalId, userId, userData = {}) {
    try {
      if (!hospitalId || !userId) throw new Error("Hospital ID and user ID are required.");
      const payload = toLocalUserPayload(hospitalId, { ...userData, id: userId });
      const saved = await localApiClient.patch(`/users/${encodeURIComponent(hospitalId)}/${encodeURIComponent(userId)}`, payload);
      return fromLocalUser(saved);
    } catch (error) {
      wrapLocalError("Update local user", error);
    }
  },

  async assignRoleToUser(hospitalId, userId, roleId) {
    try {
      if (!hospitalId || !userId || !roleId) throw new Error("Hospital ID, user ID, and role ID are required.");
      const saved = await localApiClient.post(`/users/${encodeURIComponent(hospitalId)}/${encodeURIComponent(userId)}/roles`, { roleId });
      return fromLocalUser(saved);
    } catch (error) {
      wrapLocalError("Assign local user role", error);
    }
  },

  async setUserStatus(hospitalId, userId, disabled) {
    try {
      if (!hospitalId || !userId) throw new Error("Hospital ID and user ID are required.");
      const saved = await localApiClient.patch(`/users/${encodeURIComponent(hospitalId)}/${encodeURIComponent(userId)}/status`, { disabled });
      return fromLocalUser(saved);
    } catch (error) {
      wrapLocalError("Update local user status", error);
    }
  },

  async resetPassword(hospitalId, userId, password) {
    try {
      if (!hospitalId || !userId) throw new Error("Hospital ID and user ID are required.");
      if (!password || String(password).length < 6) throw new Error("Password must be at least 6 characters.");
      const data = await localApiClient.post(`/users/${encodeURIComponent(hospitalId)}/${encodeURIComponent(userId)}/reset-password`, { password });
      return { ...data, user: fromLocalUser(data?.user || {}) };
    } catch (error) {
      wrapLocalError("Reset local user password", error);
    }
  },

  async fetchUsers(hospitalId, pageSize = 100) {
    try {
      if (!hospitalId) throw new Error("Hospital ID is required.");
      const rows = await localApiClient.get(`/users/${encodeURIComponent(hospitalId)}`);
      const staff = (Array.isArray(rows) ? rows : []).map(fromLocalUser).slice(0, pageSize);
      return {
        staff,
        lastDoc: null,
        totalUsers: staff.length,
        hasMore: staff.length === pageSize,
      };
    } catch (error) {
      wrapLocalError("Fetch local users", error);
    }
  },

  async fetchUsersByDepartment(hospitalId, departmentId) {
    const { staff } = await this.fetchUsers(hospitalId, 500);
    const scoped = staff.filter((user) => !departmentId || user.departmentId === departmentId);
    return scoped.map((user) => ({
      id: user.id,
      name: user.name,
      roleName: user.roleName,
      phoneNumber: user.phoneNumber,
      email: user.email,
    }));
  },

  async searchConsultantsByKeyword(hospitalId, searchTerm = "", roleId = null, clinicId = null) {
    const { staff } = await this.fetchUsers(hospitalId, 500);
    const q = lower(searchTerm);
    return filterByClinicIfAvailable(staff, clinicId).filter((user) => {
      if (roleId && user.roleId && user.roleId !== roleId) return false;
      if (!q) return true;
      return [user.name, user.email, user.phoneNumber, user.roleName, user.specialty, user.departmentName, user.clinicName]
        .map(lower)
        .join(" ")
        .includes(q);
    });
  },

  async fetchUsersByClinic(hospitalId, clinicId) {
    const { staff } = await this.fetchUsers(hospitalId, 500);
    return filterByClinicIfAvailable(staff.filter(isDoctor), clinicId).map((user) => ({
      id: user.id,
      name: user.name || user.email,
      roleName: user.roleName,
      email: user.email,
      specialty: user.specialty,
      clinicId: user.clinicId,
    }));
  },

  async fetchDoctorsBySpecialization(hospitalId, specialization) {
    const { staff } = await this.fetchUsers(hospitalId, 500);
    const q = lower(specialization);
    return staff.filter((user) => isDoctor(user) && (!q || lower(user.specialty).includes(q) || lower(user.roleName).includes(q)));
  },
};

export default localUserService;
